// ServiceRequest.js - PART 1

import React, { useEffect, useMemo, useState, useRef, } from "react";
import axios from "../../../utills/api";
import { Card, CardBody, CardHeader, Modal, ModalBody, ModalHeader, Button, Container, Row, } from "reactstrap";
import TableContainer from "../../../Components/Common/TableContainer";
import { FiTrash2, FiEye, FiMessageCircle, } from "react-icons/fi";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNotifications } from "../../../Components/Context/NotificationContext";
const ServiceRequest = () => {
  document.title =
    "HMS | Housekeeping Request";

  const GUEST_API = "/requests/guest";

  const [
    serviceRequests,
    setServiceRequests
  ] = useState([]);

  const [
    showServices,
    setShowServices
  ] = useState(null);

  const [
    selectedRequest,
    setSelectedRequest
  ] = useState(null);

  const [
    deleteModal,
    setDeleteModal
  ] = useState(false);


  const {
    addNotification
  } = useNotifications();
  const lastNotified =
    useRef(0);

  const fetchGuestRequests =
    async () => {

      try {
        const res =
          await axios.get(
            GUEST_API
          );
        const data =
          Array.isArray(
            res.data
          )
            ? res.data
            : [];
        setServiceRequests(
          data
        );
        const prevCount =
          lastNotified.current || 0;
        if (
          data.length >
          prevCount
        ) {

          const newCount =
            data.length -
            prevCount;
          addNotification({
            title:
              "New Housekeeping Request",
            message:
              `${newCount} new request${newCount > 1
                ? "s"
                : ""
              } received.`,
            type:
              "info",

          });
          lastNotified.current =
            data.length;
        }
      } catch (error) {

        console.error(
          "Fetch request error:",
          error
        );

      }

    };

  useEffect(() => {
    fetchGuestRequests();
    const interval =
      setInterval(
        fetchGuestRequests,
        7000
      );
    return () =>
      clearInterval(
        interval
      );
  }, [addNotification]);

  const handleDelete =
    async () => {
      try {
        if (
          selectedRequest
        ) {
          await axios.delete(
            `/requests/guest/${selectedRequest._id}`
          );
          toast.success(
            "Request deleted successfully"
          );
          setDeleteModal(
            false
          );
          fetchGuestRequests();
        }
      } catch (error) {
        console.error(
          error
        );
        toast.error(
          "Unable to delete request"
        );
      }
    };

  const autoAssignRequest = async (request) => {

    try {

      const res = await axios.patch(
        `/requests/assign-auto/${request._id}`
      );

      const assigned = res.data.request;

      const taskUrl =
        `${window.location.origin}/housekeeping/task/${assigned._id}`;

      const message = `🧹 HOUSEKEEPING TASK

━━━━━━━━━━━━━━━━━━━━

🏨 Hotel Task Assigned

👤 Guest
${assigned.guestName}

🚪 Room
${assigned.roomNumber}

🧹 Services
${assigned.services.join("\n• ")}

⚡ Priority
${assigned.priority}

📝 Description
${assigned.description || "No special instructions"}

📅 Status
${assigned.status}

━━━━━━━━━━━━━━━━━━━━

👉 Open Task

${taskUrl}

Please click the above link to start the housekeeping task.

Thank You.
`;

      window.open(

        `https://wa.me/91${assigned.assignedMobile}?text=${encodeURIComponent(message)}`,

        "_blank"

      );

      toast.success(
        `Assigned to ${assigned.assignedName}`
      );

      fetchGuestRequests();

    }

    catch (err) {

      console.error(err);

      toast.error(
        err.response?.data?.message ||
        "Unable to assign request."
      );

    }

  };

  const generateShortId =
    (id, index) => {

      if (!id)
        return `REQHK-${index + 1}`;
      return `REQHK-${String(id).slice(-5)}`;

    };
  const columns =
    useMemo(
      () => [
        {
          header:
            "Request ID",

          id:
            "requestId",

          accessorFn:
            (row, index) =>
              generateShortId(
                row._id,
                index
              )

        },
        {
          header:
            "Guest Name",

          accessorKey:
            "guestName",

          id:
            "guestName"

        },
        {
          header:
            "Room No",

          accessorKey:
            "roomNumber",

          id:
            "roomNumber"
        },
        {
          header: "Services",
          id: "services",

          accessorFn: row =>
            row.services?.join(", ") || "-",

          cell: ({ row }) => {

            const services =
              row.original.services || [];

            return (
              <div>
                {services.slice(0, 2).join(", ")}
                {services.length > 2 && (
                  <FiEye
                    style={{
                      cursor: "pointer",
                      marginLeft: 8
                    }}
                    onClick={() =>
                      setShowServices(services)
                    }
                  />
                )}

              </div>
            );
          }
        },
        {
          header: "Description",
          accessorKey: "description",
          id: "description",
          cell: ({ row }) => (
            <span>
              {row.original.description || "-"}
            </span>

          )
        },
        {
          header:
            "Status",

          accessorKey:
            "status",
          id:
            "status",
          cell:
            ({ row }) => {
              const status =
                row.original.status;
              return (
                <span
                  className={
                    `badge ${status === "Completed"
                      ? "bg-success-subtle text-success"
                      : status === "Pending"
                        ? "bg-warning-subtle text-warning"
                        : "bg-primary-subtle text-primary"
                    }`
                  }
                >

                  {status}
                </span>
              );
            }
        },
        {
          header: "Assigned To",

          accessorKey: "assignedName",

          cell: ({ row }) => (

            row.original.assignedName ?

              <span className="badge bg-success-subtle text-success">

                {row.original.assignedName}

              </span>

              :

              <span className="badge bg-secondary-subtle text-secondary">

                Unassigned

              </span>

          )
        },
        {
          header:
            "Created At",
          accessorKey:
            "createdAt",
          id:
            "createdAt",
          cell:
            ({ row }) => (

              new Date(
                row.original.createdAt
              ).toLocaleString()
            )
        },
        {
          header: "Actions",
          id: "actions",
          cell: ({ row }) => {
            const request = row.original;

            return (
              <ul className="list-inline hstack gap-2 mb-0">

                {/* WHATSAPP */}
                <li className="list-inline-item avatar-xs">

                  <button
                    className="btn btn-sm btn-soft-success"
                    title="Assign Housekeeper"
                    onClick={() =>
                      autoAssignRequest(row.original)
                    }
                    disabled={
                      row.original.status === "Completed"
                    }
                  >
                    {
                      row.original.assignedName
                        ?
                        <i className="ri-whatsapp-line"></i>
                        :
                        <i className="ri-user-add-line"></i>
                    }
                  </button>

                </li>

                {/* DELETE */}
                <li className="list-inline-item avatar-xs">
                  <button
                    className="btn btn-sm btn-soft-danger"
                    onClick={() => {
                      setSelectedRequest(request);
                      setDeleteModal(true);
                    }}
                  >
                    <i className="las la-trash-alt"></i>
                  </button>
                </li>

              </ul>
            );
          },
        }
      ],

      []

    );
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Housekeeping Request"
            pageTitle="Housekeeping"
          />
          <Row>
            <Card>
              <CardHeader
                className="d-flex justify-content-between align-items-center"
              >
                <h5 className="mb-0">
                  Service Requests
                </h5>
              </CardHeader>
              <CardBody>
                <div
                  className="table-responsive"
                  style={{
                    whiteSpace:
                      "nowrap"
                  }}
                >
                  <TableContainer

                    columns={
                      columns
                    }

                    data={
                      serviceRequests || []
                    }

                    customPageSize={
                      10
                    }

                    isGlobalFilter={
                      true
                    }

                    isPagination={
                      true
                    }

                    SearchPlaceholder=
                    "Search requests..."

                    className=
                    "text-nowrap"
                  />
                </div>
              </CardBody>
              {
                showServices && (
                  <div
                    className="
                      position-fixed
                      bg-white
                      border
                      p-3
                      shadow
                    "
                    style={{

                      top:
                        "30%",

                      left:
                        "50%",

                      transform:
                        "translate(-50%, -50%)",

                      zIndex:
                        1050,

                      width:
                        "320px",

                      borderRadius:
                        "10px"
                    }}
                  >
                    <h6>
                      Requested Services
                    </h6>
                    <ul>
                      {
                        showServices.map(
                          (
                            service,
                            index
                          ) => (

                            <li
                              key={
                                index
                              }
                            >
                              {
                                service
                              }
                            </li>

                          )
                        )
                      }
                    </ul>
                    <Button
                      size="sm"
                      color="secondary"
                      onClick={() =>
                        setShowServices(
                          null
                        )
                      }
                    >
                      Close
                    </Button>
                  </div>
                )
              }
              {/* 
              =====================================
                    DELETE CONFIRM MODAL
              =====================================
              */}
              <Modal
                isOpen={
                  deleteModal
                }
                toggle={() =>
                  setDeleteModal(
                    false
                  )
                }
              >
                <ModalHeader
                  toggle={() =>
                    setDeleteModal(
                      false
                    )
                  }
                >
                  Confirm Delete
                </ModalHeader>
                <ModalBody>
                  <p>
                    Are you sure you want to
                    delete this housekeeping
                    request?

                  </p>
                  <div
                    className="
                      d-flex
                      justify-content-end
                    "
                  >
                    <Button
                      color="secondary"
                      className="me-2"
                      onClick={() =>
                        setDeleteModal(
                          false
                        )
                      }

                    >
                      Cancel
                    </Button>
                    <Button
                      color="danger"
                      onClick={
                        handleDelete
                      }

                    >
                      Yes, Delete
                    </Button>
                  </div>
                </ModalBody>
              </Modal>
            </Card>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ServiceRequest;